import { colors, cssVariableThemedValue } from '@creditornot/cb-ingredients';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTheme } from 'styled-components';

import CustomLegends from './CustomLegends';
import CustomTooltip from './CustomTooltip';
import { ChartData, Legends, OverrideChartStyles } from './types';

export type HorizontalBarChartProps<LegendType extends Legends> = {
  legends: LegendType;
  data: ChartData<LegendType>;
  isStacked?: boolean;
  currency?: Currency;
  summaryKey: string;
  customTickFormatter?: (value: string | number) => string;
  isLegendUiVisible?: boolean;
} & OverrideChartStyles;

// constants derived from Recharts inner logic needed for styles calculations
const innerConstants = {
  startYAxisWidth: 0.0001, // a hack to make Y axis render but don't occupy any width
  legendLineHeight: 20,
  xTickRowHeight: 10,
  xTickOverflowPart: 4,
  xAxisTopMargin: 16,
};

const configurableConstants = {
  barSize: 42,
  barGap: 40,
  unstackedBarGap: 16,
  legendTopMargin: 16,
};

const getCustomXTick =
  ({
    barGap,
    xTickOverflowPart,
    xAxisTopMargin,
    tickFormatter,
  }: {
    barGap: number;
    xTickOverflowPart: number;
    xAxisTopMargin: number;
    tickFormatter: (value: string) => string;
  }) =>
  ({ index, payload, x, y }: { index: number; payload: { value: string }; x: number; y: number }) => (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={-barGap / 2 + xTickOverflowPart + xAxisTopMargin}
        fill={`var(${cssVariableThemedValue('textSecondary')})`}
        fontSize={'12px'}
        textAnchor={index === 0 ? 'start' : 'end'}
      >
        {tickFormatter(payload.value)}
      </text>
    </g>
  );

const HorizontalBarChart = <LegendType extends Legends>({
  legends,
  data,
  currency,
  customTickFormatter,
  isStacked = true,
  isLegendUiVisible = true,
  summaryKey,
  ...overrideStyles
}: HorizontalBarChartProps<LegendType>) => {
  const { startYAxisWidth, xTickRowHeight, xTickOverflowPart, xAxisTopMargin, legendLineHeight } = innerConstants;

  const { barSize, barGap, unstackedBarGap, legendTopMargin } = {
    ...configurableConstants,
    ...overrideStyles,
  };

  const { cbDirection, cbThemeMode: themeMode } = useTheme();
  const isRtl = cbDirection === 'rtl';

  const rowsCount = data.length;
  const rowHeight = isStacked ? barSize : (barSize + unstackedBarGap) * Object.keys(legends).length - unstackedBarGap;
  const marginTop = 5 + (configurableConstants.barGap - barGap) / 2;
  const allRowsHeight = rowsCount * (rowHeight + barGap) + marginTop;
  const legendHeight = isLegendUiVisible ? legendLineHeight + legendTopMargin + 1 : 0;

  const yTickProperties = {
    textAnchor: 'start',
    transform: `translate(${8 * (isRtl ? -1 : 1)}, -${rowHeight / 2 + 15})`,
    fontWeight: 500,
    fill: `var(${cssVariableThemedValue('textPrimary')})`,
    fontSize: '14px',
    letterSpacing: '-0.154px',
    width: 0, // a hack to prevent Y axis tick text from wrapping
  };

  const tooltipCursorProperties = {
    height: rowHeight,
    transform: `translate(0, ${barGap / 2})`,
    fill: themeMode === 'light' ? colors.pepper8 : colors.salt4,
  };

  const intl = useIntl();

  const tickFormatter = useCallback(
    (amount: number | string) => {
      if (customTickFormatter) {
        return customTickFormatter(amount);
      }

      if (typeof amount === 'number') {
        return intl.formatNumber(amount, currency ? { currency, style: 'currency' } : {});
      }

      return amount;
    },
    [intl, customTickFormatter, currency],
  );

  const CustomXTick = useMemo(
    () =>
      getCustomXTick({
        barGap,
        xTickOverflowPart,
        xAxisTopMargin,
        tickFormatter,
      }),
    [barGap, xTickOverflowPart, xAxisTopMargin, tickFormatter],
  );

  const legendWrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'end',
    marginInlineStart: startYAxisWidth,
  } as const;

  return (
    <ResponsiveContainer width="100%" height={allRowsHeight + xTickRowHeight + legendHeight}>
      <BarChart
        data={data}
        margin={{ top: marginTop, right: 0, left: 0, bottom: 0 }}
        layout="vertical"
        barSize={barSize}
        barGap={unstackedBarGap}
      >
        <CartesianGrid
          height={allRowsHeight - barGap / 2}
          horizontal={false}
          stroke={themeMode === 'light' ? colors.pepper4 : colors.salt12}
        />
        <XAxis type="number" tick={CustomXTick} height={xTickRowHeight} stroke="none" reversed={isRtl} />
        <YAxis
          type="category"
          dataKey="name"
          stroke="none"
          width={startYAxisWidth}
          tick={yTickProperties}
          orientation={isRtl ? 'right' : 'left'}
        />
        <Tooltip
          content={<CustomTooltip summaryKey={summaryKey} rowHeight={rowHeight} summaryCurrency={currency} />}
          cursor={tooltipCursorProperties}
          allowEscapeViewBox={{ x: false, y: true }}
        />
        {isLegendUiVisible && (
          <Legend
            height={legendHeight}
            wrapperStyle={legendWrapperStyle}
            content={<CustomLegends legends={Object.entries(legends).map(([, value]) => value)} />}
          />
        )}
        {Object.entries(legends).map(([key, { value, color }]) => (
          <Bar
            unit={currency}
            key={key}
            dataKey={key}
            name={value}
            stackId={isStacked ? 0 : undefined}
            fill={color}
            radius={[0, 2, 2, 0]}
            layout="horizontal"
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default HorizontalBarChart;
